import { useState, useEffect, useCallback } from 'react';
import type { SeatHistoryPoint } from '@techgitter/contracts';
import { fetchSeatHistory } from '../services/seatLedgerApi';

export interface UseSeatHistoryResult {
  points: SeatHistoryPoint[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Loads the computed seat-history series for a subscription. Passing `null`
 * clears the series. Cleans up in-flight requests via AbortController.
 */
export function useSeatHistory(subscriptionId: string | null): UseSeatHistoryResult {
  const [points, setPoints] = useState<SeatHistoryPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!subscriptionId) {
      setPoints([]);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    fetchSeatHistory(subscriptionId, {}, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setPoints(data);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load seat history');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [subscriptionId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  return { points, isLoading, error, refresh };
}
